import { forwardRef } from "react";
import { dayjs } from "@boo/shared";
import type { PaymentProfile } from "@boo/shared";
import { formatBaht } from "../lib/billMath";
import type { BillEditor } from "../types/bills.types";
import PersonAvatar from "./PersonAvatar";

interface ShareImageCardProps {
  editor: BillEditor;
  profile: PaymentProfile | null;
  qrUrl: string | null;
  link: string | null;
}

/**
 * Rendered off-screen and exported as a PNG for pasting into a chat. It is an
 * image, not themed UI, so it is fixed light (white / neutral) regardless of
 * the app theme.
 */
const ShareImageCard = forwardRef<HTMLDivElement, ShareImageCardProps>(
  function ShareImageCard({ editor, profile, qrUrl, link }, ref) {
    const friends = editor.people.filter((p) => !p.isOwner);
    return (
      <div
        ref={ref}
        className="w-[420px] bg-white p-6 font-sans text-neutral-900"
        style={{ position: "fixed", left: -10000, top: 0 }}
      >
        <div className="text-lg font-bold">{editor.title || "Bill"}</div>
        <div className="text-xs text-neutral-500">
          {dayjs(editor.date).format("D MMM YYYY")}
        </div>

        <ul className="mt-4 divide-y divide-neutral-200 border-y border-neutral-200">
          {friends.map((p) => (
            <li key={p.id} className="flex justify-between py-2 text-base">
              <span className="flex items-center gap-2">
                <PersonAvatar person={p} size={28} />
                {p.name}
              </span>
              <span className="font-semibold [font-variant-numeric:tabular-nums]">
                {formatBaht(editor.finalAmounts[p.id] ?? 0)}
              </span>
            </li>
          ))}
        </ul>

        {qrUrl && (
          <img
            src={qrUrl}
            alt="Pay QR"
            className="mx-auto mt-5 size-56 object-contain"
          />
        )}
        {profile && (
          <div className="mt-3 text-center text-sm">
            {profile.bankName && <div>{profile.bankName}</div>}
            {profile.accountNumber && (
              <div className="text-base font-semibold [font-variant-numeric:tabular-nums]">
                {profile.accountNumber}
              </div>
            )}
            {profile.accountHolderName && (
              <div className="text-neutral-500">
                {profile.accountHolderName}
              </div>
            )}
          </div>
        )}
        {link && (
          <div className="mt-4 break-all text-center text-[11px] text-neutral-500">
            Items &amp; details: {link}
          </div>
        )}
      </div>
    );
  },
);

export default ShareImageCard;
